import React, { useMemo } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { AffinityCategory } from './affinity.types';

export interface FocusOption {
  id: number;
  name: string;
}

interface Props {
  categories: AffinityCategory[];
  value: FocusOption | null;
  onChange: (value: FocusOption | null) => void;
  label?: string;
  placeholder?: string;
  inputSx?: SxProps<Theme>;
  /** Categories still loading: shown in the dropdown instead of "No options". */
  loading?: boolean;
}

// Flat searchable category picker, grouped by tier (L1/L2/L3).
export const CategoryPicker: React.FC<Props> = ({
  categories,
  value,
  onChange,
  label = 'Category',
  placeholder = 'Search…',
  inputSx,
  loading = false,
}) => {
  const options = useMemo(
    () =>
      [...categories].sort(
        (a, b) =>
          (a.tier ?? '').localeCompare(b.tier ?? '') || a.name.localeCompare(b.name),
      ),
    [categories],
  );

  return (
    <Autocomplete
      value={value ? (options.find((o) => o.id === value.id) ?? null) : null}
      onChange={(_, v) => onChange(v ? { id: v.id, name: v.name } : null)}
      options={options}
      loading={loading}
      loadingText="Loading categories…"
      groupBy={(o) => o.tier ?? '—'}
      getOptionLabel={(o) => o.name || ''}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          size="small"
          sx={inputSx}
        />
      )}
      sx={{ width: '100%' }}
    />
  );
};
